import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { DesktopLoadingChart } from "./desktop-loading-chart";
import { MobileLoadingChart } from "./mobile-loading-chart";
import { waitForPaint } from "../../utils/waitForPaint";
import "./loading-chart.css";

type LoadingChartProps = {
  onComplete: () => void;
  readyToDismiss: boolean;
};

/**
 * Tile “chart” route loader. Dismisses only after the reveal animation finishes
 * **and** the destination route reports ready (auth + panel shell).
 */
export function LoadingChart({ onComplete, readyToDismiss }: LoadingChartProps) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [revealComplete, setRevealComplete] = useState(false);
  const finishedRef = useRef(false);

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onComplete();
  }, [onComplete]);

  const handleRevealComplete = useCallback(() => {
    setRevealComplete(true);
  }, []);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  useEffect(() => {
    if (!readyToDismiss || !revealComplete) {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      await waitForPaint();
      if (cancelled) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) finish();
        });
      });
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [readyToDismiss, revealComplete, finish]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      finish();
    }, 12000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [finish]);

  return isMobile ? (
    <MobileLoadingChart onRevealComplete={handleRevealComplete} />
  ) : (
    <DesktopLoadingChart onRevealComplete={handleRevealComplete} />
  );
}
